import {
  Divider,
  Row,
  Typography,
  Input,
  Button,
  Table,
  Modal,
  Form,
  Col,
  message,
  Select,
  Menu,
  Dropdown,
  InputNumber,
} from "antd";
import React, { useEffect, useState } from "react";
import { SearchOutlined, MoreOutlined } from "@ant-design/icons";
import exportCSV from "assets/icons/exportcsv.svg";
import { Link } from "react-router-dom";
import {
  disableSubmitButton,
  emailPattern,
  formatCSVData,
  formatNumberUS,
  servicesNamePattern,
  tenDigit,
  urlPattern,
} from "utility/constant";
import { CSVLink } from "react-csv";
import {
  CreateSurgicalCenter,
  DeleteSurgery,
  ListSurgeryCenter,
} from "utility/action";
import { useCallback } from "react";
import search from "utility/search";
import SuccessModel from "utility/SuccessModel";
import { handleStatusChange } from "utility/StatusChange";
import DeleteModal from "utility/DeleteModal";
import { DeleteService } from "utility/DeleteService";
import TableSkeleton from "utility/TableSkeleton";

export default function SurgeryCenter() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [surgeryData, setSurgeryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [successModel, setSuccessModel] = useState(false);
  const [dataSource, setDataSource] = useState(surgeryData);
  const [csvData, setCsvData] = useState([]);
  const [messageApi, contextHolder] = message.useMessage();
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [deleteModelPrompt, setDeleteModelPrompt] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedValue, setSelectedValue] = useState(null);
  const [deleteId, setDeleteId] = useState("");
  const [form] = Form.useForm();
  const { Option } = Select;

  const labelKeyMap = {
    "Surgery Center Name": "surgeryCenterName",
    Phone: "surgeryCenterPhone",
    Website: "surgeryCenterWebsite",
    Zip: "surgeryCenterZip",
    Address: "surgeryCenterAddLine1",
    City: "surgeryCenterAddLine2",
    State: "surgeryCenterAddLine3",
    "Google Map Link": "surgeryMapSource",
    "Facebook Rating": "surgeryCenterFaceBookRating",
    "About Center": "aboutSurgeryCenter",
    Specialities: "surgeryCenterSpeciality",
  };

  const { headers, resData } = formatCSVData(labelKeyMap, csvData);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    setIsModalOpen(false);
    setSuccessModel(false);
    setDeleteModelPrompt(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setSuccessModel(false);
    setDeleteModelPrompt(false);
  };

  const handleFormChange = disableSubmitButton(["surgeryCenterName"])(
    form,
    setSubmitDisabled
  );

  const setSurgeryRow = function (row) {
    return {
      key: row?.id,
      searchByName: row?.searchByName,
      Action: row?.id,
      Specialties: (
        <div style={{ whiteSpace: "pre-wrap" }}>
          {row?.specailtyAndBodyPart?.map((item) => item.specialty)?.join("\n")}
        </div>
      ),
      createdAt: row?.createdAt,
      SurgeryCenterName: row?.surgeryCenterName,
      Phone: row?.surgeryCenterPhone,
      Website: row?.surgeryCenterWebsite,
    };
  };

  const columns = [
    {
      title: "Surgical Center name",
      dataIndex: "SurgeryCenterName",
    },
    {
      title: "Surgical Center Website",
      dataIndex: "Website",
    },
    {
      title: "Phone",
      dataIndex: "Phone",
      render: (number) => {
        return formatNumberUS(number);
      },
    },
    {
      title: "Specialty",
      dataIndex: "Specialties",
    },
    {
      title: "",
      dataIndex: "Action",
      className: "actions",
      render: (id) => {
        return (
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item key={1}>
                  <Link to="/surgery-center-detail" state={{ id: id }}>
                    Edit
                  </Link>
                </Menu.Item>
                <Menu.Item key={2}>
                  <Link
                    rel="noopener noreferrer"
                    onClick={() => {
                      setDeleteModelPrompt(true);
                      setDeleteId(id);
                    }}
                  >
                    Delete
                  </Link>
                </Menu.Item>
              </Menu>
            }
            overlayClassName="download-btn"
            placement="bottomRight"
          >
            <MoreOutlined />
          </Dropdown>
        );
      },
    },
  ];

  const fetchSurgeryData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await ListSurgeryCenter(999);
      const data = response.map((row) => setSurgeryRow(row));
      data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setSurgeryData(data);
      setDataSource(data);
      setCsvData(response);
    } catch (error) {
      messageApi.open({
        type: "error",
        content: "Something went wrong",
      });
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSurgeryData();
  }, [fetchSurgeryData]);

  const createSurgeryCenter = async (values) => {
    try {
      setDeleteId("");
      setCreateLoading(true);
      const inputData = {
        surgeryCenterName: values?.surgeryCenterName,
        searchByName: (values?.surgeryCenterName).toLowerCase(),
        surgeryCenterWebsite: values?.surgeryCenterWebsite,
        surgeryCenterPhone: values?.surgeryCenterPhone,
        surgeryCenterEmail: values?.surgeryCenterEmail,
        byName: "surgeryCenter",
        activeOnPortal: false,
      };
      const response = await CreateSurgicalCenter(inputData);
      const mappedData = {};
      Object.entries(response).forEach(([key, value]) => {
        if (value !== null) {
          mappedData[key] = value;
        }
      });

      const tableData = {
        searchByName: mappedData.searchByName,
        Action: mappedData.id,
        SurgeryCenterName: mappedData.surgeryCenterName,
        Phone: mappedData.surgeryCenterPhone,
        Website: mappedData.surgeryCenterWebsite,
        createdAt: mappedData?.createdAt,
        Specialties: mappedData?.specialty,
      };

      const sortByCreatedAt = (data) =>
        data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setSurgeryData((previous) => sortByCreatedAt([...previous, tableData]));
      setDataSource((previous) => sortByCreatedAt([...previous, tableData]));

      setIsModalOpen(false);
      form.resetFields();
      setSuccessModel(true);
      setTimeout(() => {
        setSuccessModel(false);
      }, 3000);
    } catch (error) {
      messageApi.open({
        type: "error",
        content: "Something went wrong",
      });
    } finally {
      setCreateLoading(false);
    }
  };

  const handleSearch = async (e) => {
    const currValue = e.target.value.trim();
    const searchFields = [
      "SurgeryCenterName",
      "Website",
      "Phone",
      "Speciality",
    ];
    const filteredData = search(surgeryData, currValue, searchFields);
    setDataSource(filteredData);
  };

  const handleChange = async (value) => {
    try {
      setSelectedValue(value);
      setLoading(true);
      const filterData = await handleStatusChange(
        value,
        setSurgeryRow,
        ListSurgeryCenter,
        surgeryData
      );
      setDataSource(filterData);
    } catch (error) {
      messageApi.open({
        type: "error",
        content: "Something went wrong",
      });
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const deleteIcCenter = async () => {
    try {
      setDeleteLoading(true);
      await DeleteService(deleteId, DeleteSurgery);
      setDeleteModelPrompt(false);
      setSuccessModel(true);
      setSelectedValue(null);
      fetchSurgeryData();
      setTimeout(() => {
        setSuccessModel(false);
      }, 3000);
    } catch (error) {
      console.log(error);
      messageApi.open({
        type: "error",
        content: "Something went wrong",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      {contextHolder}
      <Typography.Title level={4} className="text-center mb-0">
        Surgical Center
      </Typography.Title>
      <Divider />

      <Row justify="space-between table-header">
        <Row align="middle">
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search"
            className="searchbar"
            onChange={handleSearch}
            style={{ width: 250 }}
          />
        </Row>

        <Row className="gap-10 mtr-20 bold-font filter-dropdown-list">
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 100 }}
            onChange={handleChange}
            getPopupContainer={(trigger) => trigger.parentNode}
            value={selectedValue}
          >
            <Option value="ACTIVE">ACTIVE</Option>
            <Option value="INACTIVE">INACTIVE</Option>
          </Select>
          <CSVLink
            data={resData}
            headers={headers}
            filename={"Surgery Center.csv"}
            className="export-btn"
          >
            <img src={exportCSV} alt="" className="action-icons" />
          </CSVLink>
          <Button className="blue-btn inherit-btn" onClick={showModal}>
            Add New
          </Button>
        </Row>
      </Row>

      {loading ? (
        <TableSkeleton />
      ) : (
        <Table
          columns={columns}
          dataSource={dataSource}
          className="common-table"
          scroll={{ x: 992 }}
          pagination={{ position: ["", "bottomCenter"] }}
        />
      )}

      <Modal
        className="form-modal"
        centered
        footer={null}
        width={700}
        title={
          <Typography.Title level={3} className="modal-title">
            Add Surgical Center
          </Typography.Title>
        }
        visible={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <Form
          name="basic"
          onFinish={createSurgeryCenter}
          autoComplete="off"
          layout="vertical"
          form={form}
          onFieldsChange={handleFormChange}
        >
          <Row gutter={20}>
            <Col span={12} md={12} xs={24} sm={24}>
              <Form.Item
                label="Surgical center name"
                name="surgeryCenterName"
                rules={[
                  {
                    required: true,
                    message: "Please enter a name",
                  },
                  {
                    pattern: servicesNamePattern,
                    message: "Please enter a valid name",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={12} md={12} xs={24} sm={24}>
              <Form.Item
                label="Surgical center website"
                name="surgeryCenterWebsite"
                rules={[
                  {
                    pattern: urlPattern,
                    message: "Please input valid url",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={12} md={12} xs={24} sm={24}>
              <Form.Item
                label="Surgical center phone"
                name="surgeryCenterPhone"
                rules={[
                  {
                    pattern: tenDigit,
                    message: "Please enter a valid phone number",
                  },
                ]}
              >
                <InputNumber
                  controls={false}
                  formatter={(value) => {
                    return formatNumberUS(value);
                  }}
                  parser={(value) => {
                    const cleanedValue = value.replace(/\D/g, "");
                    return cleanedValue.slice(0, 10);
                  }}
                  style={{ width: "100%", borderRadius: "5px" }}
                />
              </Form.Item>
            </Col>

            <Col span={12} md={12} xs={24} sm={24}>
              <Form.Item
                label="Surgical center email"
                name="surgeryCenterEmail"
                rules={[
                  {
                    pattern: emailPattern,
                    message: "Please enter a valid email",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={24} md={24} xs={24} sm={24} className="center-btn">
              <Button
                className="theme-btn"
                htmlType="submit"
                loading={createLoading}
                disabled={submitDisabled}
              >
                Submit
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>

      <SuccessModel
        message={
          deleteId !== ""
            ? "Surgical Center Deleted Successfully"
            : "Surgical Center Added Successfully"
        }
        open={successModel}
      />

      <DeleteModal
        content="Are you sure you want to delete the surgical center?"
        open={deleteModelPrompt}
        onClick={deleteIcCenter}
        buttonLoading={deleteLoading}
        onOk={handleOk}
        onCancel={handleCancel}
        buttonText="Yes"
      />
    </>
  );
}
